import React, { useId, useState, useRef, useEffect, useLayoutEffect } from "react"
import { createPortal } from "react-dom"
import { MdInfoOutline } from "react-icons/md"
import styles from "./infoTooltip.module.css"

const InfoTooltip = ({
    text,
    preferredPosition = "top",
    delay = 200,
    children,
    className = "",
    ariaLabel = "Mais informações",
    stopPropagation = false,
}) => {
    const tooltipId = useId()
    const [visible, setVisible] = useState(false)
    const [positioned, setPositioned] = useState(false)
    const [position, setPosition] = useState(preferredPosition)
    const [coords, setCoords] = useState({ top: 0, left: 0 })
    const timeoutRef = useRef(null)
    const triggerRef = useRef(null)
    const tooltipRef = useRef(null)

    const show = () => {
        clearTimeout(timeoutRef.current)
        setPositioned(false)
        setVisible(true)
    }

    const showWithDelay = () => {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = setTimeout(() => {
            show()
        }, delay)
    }

    const hide = () => {
        clearTimeout(timeoutRef.current)
        setPositioned(false)
        setVisible(false)
    }

    const toggleMobile = event => {
        if (stopPropagation) event?.stopPropagation()
        clearTimeout(timeoutRef.current)
        setPositioned(false)
        setVisible(true)
    }

    const handleKeyDown = event => {
        if (event.key === "Escape") hide()
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            toggleMobile(event)
        }
    }

    useLayoutEffect(() => {
        if (!visible) return

        const rect = triggerRef.current?.getBoundingClientRect()
        const tooltipEl = tooltipRef.current

        if (!rect || !tooltipEl) return

        const tooltipRect = tooltipEl.getBoundingClientRect()
        const margin = 12

        let top = 0
        let left = 0
        let finalPosition = preferredPosition

        const fitsTop = rect.top > tooltipRect.height + margin
        const fitsBottom =
            window.innerHeight - rect.bottom > tooltipRect.height + margin

        if (preferredPosition === "top" && !fitsTop && fitsBottom) {
            finalPosition = "bottom"
        }

        if (preferredPosition === "bottom" && !fitsBottom && fitsTop) {
            finalPosition = "top"
        }

        if (finalPosition === "top") {
            top = rect.top - tooltipRect.height - margin
            left = rect.left + rect.width / 2 - tooltipRect.width / 2
        } else {
            top = rect.bottom + margin
            left = rect.left + rect.width / 2 - tooltipRect.width / 2
        }

        // prevent lateral overflow
        if (left < 8) left = 8
        if (left + tooltipRect.width > window.innerWidth - 8) {
            left = window.innerWidth - tooltipRect.width - 8
        }

        setPosition(finalPosition)
        setCoords({ top, left })
        setPositioned(true)
    }, [visible, preferredPosition])

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (triggerRef.current && !triggerRef.current.contains(e.target)) {
                setVisible(false)
            }
        }

        document.addEventListener("mousedown", handleClickOutside)
        return () =>
            document.removeEventListener("mousedown", handleClickOutside)
    }, [])

    useEffect(() => () => clearTimeout(timeoutRef.current), [])

    return (
        <>
            <span
                ref={triggerRef}
                onMouseEnter={showWithDelay}
                onMouseLeave={hide}
                onClick={toggleMobile}
                onFocus={show}
                onBlur={hide}
                onKeyDown={handleKeyDown}
                className={`${styles.trigger} ${className}`}
                aria-label={ariaLabel}
                aria-describedby={visible ? tooltipId : undefined}
                aria-expanded={visible}
                role="button"
                tabIndex="0"
            >
                {children || <MdInfoOutline />}
            </span>

            {visible &&
                createPortal(
                    <div
                        ref={tooltipRef}
                        className={`${styles.tooltip} ${styles[position]}`}
                        style={{
                            top: coords.top,
                            left: coords.left,
                            position: "fixed",
                            visibility: positioned ? "visible" : "hidden"
                        }}
                        role="tooltip"
                        id={tooltipId}
                    >
                        {text}
                        <div className={`${styles.arrow} ${styles[position]}`} />
                    </div>,
                    document.body
                )}
        </>
    )
}

export default InfoTooltip
